import { practiceSurfaceAccess } from '../lib/practice-access.ts';
import { type JSX, type ReactNode, useEffect, useRef } from 'react';
import { Navigate, Outlet, matchPath, useLocation, Link } from 'react-router-dom';
import { SESSION_ROUTE_PATTERNS } from '../lib/session-routes.ts';
import { useAuth } from '../lib/auth-context.tsx';
import { useMeGate } from '../lib/use-me.ts';
import { BootstrapConnectionError } from './BootstrapConnectionError.tsx';
import { derivePolicyGate, isPolicyGateRoute, isReachableWhileRestricted, policyGateRoute } from '../lib/policy-gate.ts';
import { useTokenStore } from '../api/use-api.ts';
import { drain, listEntries, setQueueViewer } from '../lib/offline/queue.ts';
import { SubscriptionBanner, SubscriptionEndedBanner, SubscriptionReadOnlyBanner } from './SubscriptionBanner.tsx';
import { DpaNoticeBanner } from './DpaNoticeBanner.tsx';
import { IcoRegistrationBanner } from './IcoRegistrationBanner.tsx';
import { TrialEndingBanner } from './TrialEndingBanner.tsx';
import { trialDaysRemaining, TRIAL_WARNING_DAYS } from '../lib/trial.ts';
import { PrivacyNoticeBanner } from './PrivacyNoticeBanner.tsx';
import { AutoMasteryNoticeBanner } from './AutoMasteryNoticeBanner.tsx';
import { SubscriptionInterstitial } from './SubscriptionInterstitial.tsx';
import { AppHeader } from './AppHeader.tsx';
import { AppSidebar } from './AppSidebar.tsx';
import { capShellBanners, type ShellBannerDescriptor } from '../lib/shell-banners.ts';
import { practiceSetupOwed } from '../lib/practice-setup-steps.ts';
import { PendingActionsBanner } from './PendingActionsBanner.tsx';
import { usePendingActionsState } from './use-pending-actions.ts';
import { RoleIntroOverlay } from './RoleIntro/RoleIntroOverlay.tsx';
import { ExperienceFeedbackPrompt } from './ExperienceFeedbackPrompt.tsx';
import { mfaPath, mfaStep } from '../lib/mfa-step.ts';

function PrivacyFooterLink(): JSX.Element {
  return (
    <footer className="mt-12 border-t border-border px-4 py-6 text-xs text-muted-foreground">
      <Link to="/privacy" className="hover:text-foreground hover:underline">
        Privacy notice
      </Link>
    </footer>
  );
}

/**
 * The ONE shell for authenticated surfaces that render no nav — with the chrome, every nav
 * item redirects straight back (v7.87). One, not three: a shell cannot forget what it does not render.
 */
function ChromelessShell(props: {
  readonly privacyNoticeBannerDue: boolean | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Raw flag, never inverted — sub-processor-disclosure-completeness pins this shape. */}
      {props.privacyNoticeBannerDue === true && <PrivacyNoticeBanner />}
      <div className="flex flex-1 flex-col">{props.children}</div>
      <PrivacyFooterLink />
    </div>
  );
}

/**
 * Authenticated app shell + subscription gate (spec §6 lines 1105, 1387–1388).
 *
 * 1. Redirects to /login when there is no in-memory access token.
 * 2. Fetches GET /me (exempt from the server subscription gate) and reads the
 *    live subscription status.
 * 3. cancelled / suspended → full-screen interstitial (mirrors the server's 402
 *    SUBSCRIPTION_RESTRICTED on clinical routes; only billing remains reachable).
 *    past_due → non-blocking warning banner above the routed content.
 *
 * The gate never grants access the API denies - every clinical endpoint is
 * independently gated server-side (src/lib/subscription-gate.ts).
 */
/**
 * App-shell offline-queue recovery (2026-07-06 P0-A): data
 * entries queued in a session screen that was closed or abandoned used to sync
 * ONLY if the user re-opened that exact screen - "they will sync when you
 * reconnect" was otherwise false. Drain once on shell mount (app open / login)
 * and again whenever connectivity returns, from anywhere in the app. Replays
 * are idempotent and drains are serialised in the queue module, so this never
 * conflicts with a live session screen's own drains.
 */
function useGlobalQueueDrain(enabled: boolean, userId: string | null): void {
  const store = useTokenStore();
  const storeRef = useRef(store);
  storeRef.current = store;
  useEffect(() => {
    void setQueueViewer(userId ?? undefined);
  }, [userId]);
  useEffect(() => {
    if (!enabled) return;
    const drainIfAny = () => {
      void listEntries(userId ?? undefined).then((all) => {
        // Drain scoped to the signed-in user - on a shared device another
        // user's queued entries must never sync under this user's token
        // (recorded_by provenance). They drain when their owner signs in.
        if (all.length > 0) void drain(storeRef.current, userId ?? undefined);
      });
    };
    drainIfAny();
    window.addEventListener('online', drainIfAny);
    return () => window.removeEventListener('online', drainIfAny);
  }, [enabled, userId]);
}

export function ProtectedLayout(): JSX.Element {
  const { accessToken, bootstrapped, bootstrapError, userId } = useAuth();
  const me = useMeGate();
  useGlobalQueueDrain(bootstrapped && !!accessToken, userId);

  // A live data-collection session is a full-screen FOCUS surface: it renders
  // without the app header/sidebar so the SessionRecorder's own exit control -
  // which routes through the "this session is still open" nudge - is the ONLY
  // way out. With the chrome present, every sidebar/header link (case nav, brand
  // logo, Profile, Sign out) is a silent bypass that leaves an
  // in-progress session behind: the zombie-session class the nudge exists to
  // prevent. BrowserRouter has no navigation blocker to guard those links, so
  // removing the competing affordances is the only reliable fix. Detected from
  // the shared SESSION_ROUTE_PATTERNS (same source App.tsx registers the routes
  // from) so the two lists can never drift. Called before the gate early-returns
  // so it applies to both session routes; hook order stays unconditional.
  const { pathname, search } = useLocation();
  const isSessionRoute = SESSION_ROUTE_PATTERNS.some((p) => matchPath(p, pathname) != null);
  const pendingActions = usePendingActionsState();

  // Wait for the on-load silent refresh before deciding to redirect - a reload
  // clears the in-memory token, and the refresh cookie may still restore it.
  if (!bootstrapped) {
    if (bootstrapError === 'network') return <BootstrapConnectionError />;
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!accessToken) return <Navigate to="/login" replace />;

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  // MFA gate: the SERVER decides whether the session is held (mfaGate), so the
  // shell can no longer admit one the API refuses. Everything below reads fields
  // the narrow payload does not carry, so this must return, never fall through.
  // Before the email gate: an emailed code proves the inbox, so a new account is asked once.
  if (me.data?.mfaGate === 'pending') {
    return <Navigate to={mfaPath(mfaStep(me.data))} state={{ from: pathname + search }} replace />;
  }

  // Email-verification gate (v7.26): reached only by a session that passed MFA without
  // an emailed code. /verify-email renders outside this layout, so this does not loop.
  if (me.data && !me.data.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // A cancelled/suspended plan is the PRACTICE's: the interstitial replaces only that
  // practice's own routes (its plan screen excepted, the way back); elsewhere a banner per
  // such practice, as for a free period ending.
  const practiceRouteMatch = matchPath('/practices/:practiceId/*', pathname);
  const routePracticeId = practiceRouteMatch?.params['practiceId'] ?? null;
  const routePractice = routePracticeId === null
    ? null
    : (me.data?.memberships ?? []).find((m) => m.practiceId === routePracticeId) ?? null;
  // The plan page, the policy gate's own pages — the gate below sends an owner with stale
  // terms to /terms first, so the interstitial must not stand in front of it — AND the export
  // screen, which binding text promises stays reachable in exactly these two statuses.
  const reachable = isReachableWhileRestricted(pathname);
  if (routePractice !== null && !reachable && routePractice.planOnly) {
    return (
      <ChromelessShell privacyNoticeBannerDue={me.data?.privacyNoticeBannerDue}>
        <SubscriptionInterstitial
          status={routePractice.subscriptionStatus}
          practiceId={routePractice.practiceId}
          practiceName={routePractice.practiceName}
          canManagePlan={routePractice.capabilities.includes('practice.back_office')}
          canExport={routePractice.capabilities.includes('practice.govern')}
        />
      </ChromelessShell>
    );
  }

  // POLICY gate — the Terms of Service and the DPA, decided together by
  // derivePolicyGate so the two can never race to redirect. Terms come first:
  // they are the Principal Agreement and the DPA is subject to them.
  //
  // Runs AFTER the subscription interstitial, not before: every one of
  // `GET /practices/:id/terms`, `POST .../terms/accept`, `GET .../dpa` and
  // `POST .../dpa/accept` sits behind requireSubscription, so redirecting a
  // cancelled/suspended owner here would hand them a screen whose data 402s and
  // whose accept button 402s - the offered-but-refused class v7.87 closed.
  // Subscription first, then this.
  //
  // Owner-only and asymmetric by design (DPA clause 16.2 for the DPA half; the ToS
  // half has no notice state at all — see derivePolicyGate):
  //   never-signed  → block; the practice holds no data, so nothing is lost.
  //   stale + overdue → block the OWNER only; their staff keep working.
  //   stale + in notice → no block at all (DpaNoticeBanner nudges instead).
  // The overdue block covers the owner's whole surface, export included - clause 16.2 stopped
  // promising otherwise (owner 2026-09-04) because accepting lifts it immediately and their
  // staff are never blocked, so nothing is withheld that the owner cannot restore themselves.
  //
  // Placed after MFA so we never flash this before identity is settled. The
  // target route renders inside this layout, so the redirect is suppressed once
  // we are already on it - otherwise it would loop.
  //
  // Exempt from a live data-collection session: yanking a recorder out mid-session
  // to sign a document loses the in-progress data, which is a worse outcome than a
  // few minutes' delay. The block re-applies the moment the session is exited.
  // NOTE the exemption is WIDER for the Terms than for the DPA, and the DPA's old
  // justification does not carry over: "a never-signed practice has no cases to
  // record against" is true of the DPA (nothing clinical works before it) but FALSE
  // of the Terms, because every practice already operating became tosStatus 'never'
  // the moment acceptance was wired. So an owner mid-session can finish that session
  // before accepting. Deliberate: the alternative is destroying live clinical data to
  // force a contract, and the exposure is one session long.
  const policyGate = derivePolicyGate(me.data?.memberships ?? [], Date.now());
  const policyTarget = policyGateRoute(policyGate);
  if (policyTarget !== null && !isSessionRoute) {
    if (pathname !== policyTarget) return <Navigate to={policyTarget} replace />;
    return (
      <ChromelessShell privacyNoticeBannerDue={me.data?.privacyNoticeBannerDue}>
        <Outlet />
      </ChromelessShell>
    );
  }


  // ROLE ACKNOWLEDGEMENT gate (ToS 4.2), one gate for everyone. POLICY GATE routes are
  // exempt: their accept awaits ['me'] before navigating, so this would capture THEM.
  const acknowledgementDue = (me.data?.roleAcknowledgementDue ?? []).length > 0;
  if (acknowledgementDue && !isSessionRoute && !isPolicyGateRoute(pathname)) {
    if (pathname !== '/role-acknowledgement') {
      // Carry the attempted route: unlike the policy gate, whose destination the shell
      // re-derives, this one can interrupt a one-time hop that nothing would send the user
      // back to. `+ search`, not the path alone, so a query string survives the round trip.
      return <Navigate to="/role-acknowledgement" state={{ from: pathname + search }} replace />;
    }
    return (
      <ChromelessShell privacyNoticeBannerDue={me.data?.privacyNoticeBannerDue}>
        <Outlet />
      </ChromelessShell>
    );
  }

  // Full-screen focus surface for live sessions - no chrome, and no past_due banner (its
  // billing link is itself a nudge bypass); it returns the moment the session is exited.
  // Every access gate above has already run, so recording stays gated.
  if (isSessionRoute) {
    return (
      <div className="min-h-screen bg-background">
        <Outlet />
      </div>
    );
  }

  const icoFlaggedMembership =
    (me.data?.memberships ?? []).find(
      (m) =>
        m.capabilities.includes('practice.govern') &&
        !practiceSetupOwed(m) &&
        (m.icoRegistrationNumber === null ||
          (m.icoRegistrationExpiresAt !== null && m.icoRegistrationExpiresAt <= Date.now())),
    ) ?? null;

  const rest: ShellBannerDescriptor<JSX.Element>[] = [];
  // FB-0021: in the shell, because a client-only account never reaches the dashboard.
  const pendingCount = pendingActions.invitations.length + pendingActions.contracts.length;
  if (pendingCount > 0) {
    rest.push({
      key: 'pending-actions',
      element: <PendingActionsBanner key="pending-actions" count={pendingCount} />,
      label: 'Invitation or agreement waiting',
      href: '/pending',
    });
  }
  // ONE BANNER PER PRACTICE whose plan needs attention: a member of two practices hears about both.
  // The plan page is back-office authority's to act on; everyone else is told, and pointed at
  // the practice overview, where the interstitial says whom to ask.
  const planLinkFor = (m: { practiceId: string; capabilities: string[] }): string =>
    practiceSurfaceAccess(m).canSeePlan ? `/practices/${m.practiceId}/plan` : `/practices/${m.practiceId}`;
  for (const m of me.data?.memberships ?? []) {
    if (m.subscriptionStatus === 'past_due') {
      rest.push({
        key: `subscription-${m.practiceId}`,
        element: <SubscriptionBanner key={`subscription-${m.practiceId}`} practiceId={m.practiceId} practiceName={m.practiceName} linkTo={planLinkFor(m)} />,
        label: 'Subscription payment overdue',
        href: planLinkFor(m),
      });
    } else if (m.planOnly) {
      rest.push({
        key: `subscription-${m.practiceId}`,
        element: (
          <SubscriptionEndedBanner
            key={`subscription-${m.practiceId}`}
            practiceId={m.practiceId}
            practiceName={m.practiceName}
            // planOnly is true only for cancelled/suspended or the 14-day ladder rung, so
            // `subscriptionStatus` is never null or 'active'/'past_due' here in practice —
            // 'pilot' is the harmless fallback the type system still requires.
            status={
              m.subscriptionStatus === 'cancelled' || m.subscriptionStatus === 'suspended'
                ? m.subscriptionStatus
                : 'pilot'
            }
            linkTo={planLinkFor(m)}
          />
        ),
        label: 'Practice plan ended',
        href: planLinkFor(m),
      });
    } else if (m.readOnly) {
      rest.push({
        key: `subscription-${m.practiceId}`,
        element: (
          <SubscriptionReadOnlyBanner
            key={`subscription-${m.practiceId}`}
            practiceId={m.practiceId}
            practiceName={m.practiceName}
            linkTo={planLinkFor(m)}
          />
        ),
        label: 'Practice is read-only',
        href: planLinkFor(m),
      });
    }
  }
  if (policyGate.noticePracticeId !== null) {
    rest.push({
      key: 'dpa',
      element: (
        <DpaNoticeBanner
          key="dpa"
          practiceId={policyGate.noticePracticeId}
          dueAt={policyGate.noticeDueAt}
        />
      ),
      label: 'Data processing agreement update',
      href: `/practices/${policyGate.noticePracticeId}/dpa`,
    });
  }
  if (icoFlaggedMembership !== null) {
    rest.push({
      key: 'ico',
      element: (
        <IcoRegistrationBanner
          key="ico"
          practiceId={icoFlaggedMembership.practiceId}
          icoRegistrationNumber={icoFlaggedMembership.icoRegistrationNumber}
          icoRegistrationExpiresAt={icoFlaggedMembership.icoRegistrationExpiresAt}
        />
      ),
      label: 'ICO registration',
      href: `/practices/${icoFlaggedMembership.practiceId}/settings`,
    });
  }
  // ONE BANNER PER PRACTICE whose free run is ending, never a chosen one: picking would
  // leave a member of two practices unwarned about the other. capShellBanners caps them.
  // NOT gated on practiceSetupOwed: `setup_completed_at` is stamped only by the explicit
  // "Finish", so an owner who exits the round early never saw this at 14 days, at 0, or after
  // it lapsed — off by default for exactly the person it names.
  for (const m of me.data?.memberships ?? []) {
    if (m.trialEndsAt === null) continue;
    const days = trialDaysRemaining(m.trialEndsAt, Date.now());
    if (days > TRIAL_WARNING_DAYS) continue;
    rest.push({
      key: `trial-${m.practiceId}`,
      element: (
        <TrialEndingBanner
          key={`trial-${m.practiceId}`}
          practiceId={m.practiceId}
          practiceName={m.practiceName}
          daysRemaining={days}
          heldSlots={m.heldSlots}
          linkTo={planLinkFor(m)}
        />
      ),
      label: days < 0 ? 'Free period ended' : 'Free period ending',
      href: planLinkFor(m),
    });
  }
  // One notice, one dismissal: the first practice it is due in names it.
  const autoMastery = me.data?.memberships.find((m) => m.autoMasteryNoticeDue);
  if (autoMastery !== undefined) {
    rest.push({
      key: 'auto-mastery',
      element: (
        <AutoMasteryNoticeBanner
          key="auto-mastery"
          practiceId={autoMastery.practiceId}
          practiceName={autoMastery.practiceName}
          canChangeSetting={autoMastery.capabilities.includes('practice.govern')}
        />
      ),
      label: 'Targets now master automatically',
      href: `/practices/${autoMastery.practiceId}`,
    });
  }
  const privacyBanner: ShellBannerDescriptor<JSX.Element> | null =
    me.data?.privacyNoticeBannerDue === true
      ? {
          key: 'privacy',
          element: <PrivacyNoticeBanner key="privacy" />,
          label: me.data.privacyNoticeUpdatedSinceSeen ? 'Privacy notice update' : 'Privacy notice',
          href: '/privacy',
        }
      : null;
  const { shown, suppressed } = capShellBanners(privacyBanner, rest);

  // Banners stay the shell's DIRECT first child (sub-processor-disclosure-completeness
  // asserts it): full-bleed above everything is the most prominent placement, and a
  // wrapper is how a notice gets buried while the source still reads as if it renders.
  // The rail is then a SIBLING of the header column, so it runs full height.
  return (
    <div className="min-h-screen bg-background">
      {shown.map((b) => b.element)}
      {suppressed.length > 0 && (
        <div
          role="status"
          data-testid="banner-overflow"
          className="border-b border-border bg-muted px-4 py-1 text-xs text-muted-foreground"
        >
          {suppressed.map((b, i) => (
            <span key={b.key}>
              {i > 0 && ', '}
              <Link to={b.href} className="underline underline-offset-2 hover:text-foreground">
                {b.label}
              </Link>
            </span>
          ))}
        </div>
      )}
      {/* HERE, not on the dashboard, for the pending-actions banner's reason above: a
          client-only account never reaches DashboardScreen. AFTER the banners, never before:
          the privacy notice is pinned as this shell's first child. It portals, so it yields. */}
      <RoleIntroOverlay />
      <ExperienceFeedbackPrompt />
      <div className="flex">
        <AppSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <AppHeader />
          <main className="min-w-0 flex-1">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
